import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { doSignOutClear } from "../../components/auth/helpers";
import { Ticker, TickerInfo } from "../../assets/interfaces/interfaces";
import { fetchCompanyData } from "../../components/api/fetchCompanyData";

type WatchlistProps = {
  tickers: Ticker[];
  searchResults: Ticker[];
  isLoading: boolean;
  removedIds: Array<string>;
  reorderIds: Array<string>;
  isMutating: boolean;
  isWatchlistDrawerOpen: boolean;
  isHighlightBadge: boolean;
  error: string | null;
  isAdding: boolean;
  watchlistTickersData: TickerInfo; // for tickers in watchlist
  searchResultsTickersData: TickerInfo; // for tickers in search results
};

const initialState: WatchlistProps = {
  tickers: [],
  searchResults: [],
  isLoading: false,
  removedIds: [],
  reorderIds: [],
  isMutating: false,
  isWatchlistDrawerOpen: false,
  isHighlightBadge: false,
  error: null,
  isAdding: false,
  watchlistTickersData: {},
  searchResultsTickersData: {},
};

export const getTickersInWatchlist = createAsyncThunk(
  "watchlist/fetchTickers",
  async ({ userId }: { userId: string }, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await axios.get(
        `${process.env.REACT_APP_SPL_URL}/v1/watchlist/${userId}`,
        { withCredentials: true },
      );
      return data.data;
    }
    catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response.status === 403) {
          doSignOutClear(dispatch);
          return;
        }
        if (error.response) {
          return rejectWithValue(error.response.data);
        }
      }
      return rejectWithValue("Unexpected error occurred");
    }
  },
);

export const searchTickerResults = createAsyncThunk(
  "watchlist/searchTickers",
  async (arg: string, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await axios.get(
        `${process.env.REACT_APP_SPL_URL}/search/${arg}`,
        { withCredentials: true },
      );
      return data;
    }
    catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response.status === 403) {
          doSignOutClear(dispatch);
          return;
        }
        if (error.response) {
          return rejectWithValue(error.response.data);
        }
      }
      return rejectWithValue("Unexpected error occurred");
    }
  },
);

export const deleteTickers = createAsyncThunk(
  "watchlist/deleteTickers",
  async (
    { ids, userId }: { ids: string[]; userId: string },
    { rejectWithValue, dispatch },
  ) => {
    try {
      await axios.delete(
        `${process.env.REACT_APP_SPL_URL}/v1/watchlist/${userId}/tickers`,
        {
          data: { tickers: ids },
          withCredentials: true,
        },
      );
    }
    catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response.status === 403) {
          doSignOutClear(dispatch);
          return;
        }
        if (error.response) {
          return rejectWithValue(error.response.data);
        }
      }
      return rejectWithValue("Unexpected error occurred");
    }
  },
);

export const addTicker = createAsyncThunk(
  "watchlist/addTicker",
  async (
    { userId, ticker }: { userId: string; ticker: string },
    { rejectWithValue, dispatch },
  ) => {
    try {
      const query = {
        ticker,
      };
      const { data } = await axios.post(
        `${process.env.REACT_APP_SPL_URL}/v1/watchlist/${userId}/tickers`,
        query,
        {
          headers: {
            "Content-Type": "application/json",
          },
          withCredentials: true,
        },
      );
      return data.data[0];
    }
    catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response.status === 403) {
          doSignOutClear(dispatch);
          return;
        }
        if (error.response) {
          return rejectWithValue(error.response.data.error || "Fail to add ticker");
        }
      }
      return rejectWithValue("Unexpected error occurred");
    }
  },
);

export const updateTickerSequence = createAsyncThunk(
  "watchlist/updateTickerSequence",
  async (
    {
      payload,
      userId,
    }: { payload: { ticker: string; seq: number }[]; userId: string },
    { rejectWithValue, dispatch },
  ) => {
    try {
      return await axios.put(
        `${process.env.REACT_APP_SPL_URL}/v1/watchlist/${userId}/tickers`,
        {
          sequence: payload,
        },
        { withCredentials: true },
      );
    }
    catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response.status === 403) {
          doSignOutClear(dispatch);
          return;
        }
        if (error.response) {
          return rejectWithValue(error.response.data);
        }
      }
      return rejectWithValue("Unexpected error occurred");
    }
  },
);

export const fetchTickersData = createAsyncThunk(
  "watchlist/fetchTickersData",
  async ({ tickersToFetch, isSearchMode = false }: { tickersToFetch: string[]; isSearchMode?: boolean }, { rejectWithValue }) => {
    try {
      const data = await fetchCompanyData(tickersToFetch);
      if (Array.isArray(data)) {
        return {
          isSearchMode,
          data: data.reduce((acc, item) => {
            acc[item.ticker] = {
              name: item.name,
              prev_close_price: item.prev_close_price,
              last_price: item.last_price,
            };
            return acc;
          }, {}),
        };
      }
    }
    catch (error) {
      console.error("Error fetching tickers data:", error);
      return rejectWithValue(error.message);
    }
  },
);

export const watchlistSlice = createSlice({
  name: "watchlist",
  initialState,
  reducers: {
    resetSearchResults: (state) => {
      state.searchResults = [];
      state.searchResultsTickersData = {};
    },
    removeTicker: (state, action) => {
      state.removedIds = [...state.removedIds, action.payload];
    },
    updateTickerOrder: (state, action: { type: string; payload: string[] }) => {
      state.reorderIds = action.payload;
    },
    resetRemoveList: (state) => {
      state.removedIds = [];
    },
    resetReorderList: (state) => {
      state.reorderIds = [];
    },
    setWatchlistDrawerState: (state, action) => {
      state.isWatchlistDrawerOpen = action.payload;
    },
    setIsHighlightBadge: (state, action) => {
      state.isHighlightBadge = action.payload;
    },
    resetError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getTickersInWatchlist.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getTickersInWatchlist.fulfilled, (state, action) => {
        state.isLoading = false;
        state.tickers = action.payload;
      })
      .addCase(searchTickerResults.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(searchTickerResults.fulfilled, (state, action) => {
        state.isLoading = false;
        state.searchResults = action.payload;
      })
      .addCase(addTicker.pending, (state) => {
        state.isAdding = true;
      })
      .addCase(addTicker.fulfilled, (state, action) => {
        state.isAdding = false;
        state.tickers = [action.payload, ...state.tickers]; // add new ticker to the top of the tickers list
      })
      .addCase(addTicker.rejected, (state) => {
        state.isAdding = false;
        state.error = "watchlist has reached maximum size";
      })
      .addCase(deleteTickers.pending, (state) => {
        state.isMutating = true;
      })
      .addCase(updateTickerSequence.pending, (state) => {
        state.isMutating = true;
      })
      .addCase(deleteTickers.fulfilled, (state) => {
        state.isMutating = false;
      })
      .addCase(deleteTickers.rejected, (state) => {
        state.isMutating = false;
      })
      .addCase(updateTickerSequence.fulfilled, (state) => {
        state.isMutating = false;
      })
      .addCase(updateTickerSequence.rejected, (state) => {
        state.isMutating = false;
      })
      .addCase(fetchTickersData.fulfilled, (state, action) => {
        if (action.payload.isSearchMode) {
          state.searchResultsTickersData = {
            ...state.searchResultsTickersData,
            ...action.payload.data,
          };
        }
        else {
          state.watchlistTickersData = {
            ...state.watchlistTickersData,
            ...action.payload.data,
          };
        }
      });
  },
});
export const {
  resetSearchResults,
  removeTicker,
  updateTickerOrder,
  resetRemoveList,
  resetReorderList,
  setWatchlistDrawerState,
  setIsHighlightBadge,
  resetError,
} = watchlistSlice.actions;

export default watchlistSlice.reducer;
